import React from "react";
import { Box, Tab } from "@mui/material";

import { StyledMainBox, StyledTabs } from "../../components/commonComponents/StyledComponents/styled";

import {
  fetchUsers,
  checkUserDetailsFromCookie,
  fetchRoles,
  checkUserPermissions,
  manageUsersPaginationCookie,
  normalizeUserListType,
} from "../../api_calls/userManagement";
import AccessControl from "../../components/commonComponents/Hoc/AccessControl";

import PropTypes from "prop-types";
import UserList from "./tabData/UserList";
import TenantUserList from "./tabData/TenantUserList";
import { deletePaginationCookies, scrollTableListings } from "../../api_calls/utils";
import BrandUserlist from "./tabData/BrandUserList";
import { setBatchLoadingStatus } from "../../api_calls/requestConfig";

// to load tab content
function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box mt={5}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const Users = () => {
  const [value, setValue] = React.useState(0);
  const [loadedUserTypes, setLoadedUserTypes] = React.useState([]);
  const checkUserDetails = checkUserDetailsFromCookie();
  const allowedAccessRights = checkUserPermissions();
  const permissionScope = allowedAccessRights.allowed_access;
  const userType = checkUserDetails.user_detail.user_type;
  // deactivated sections for the logged in user
  let deactivatedSections = checkUserDetails.success === true ? checkUserDetails.user_detail.deactivated_sections : [];
  let showBrandsSection = userType === "cyreen" || !deactivatedSections.includes("campaign_list");

  let tabsConfig = [
    {
      label: "Users",
      icon: "",
      unique_key: "retailerUsersList",
      user_type: "retailer",
      dashboard: <UserList usedFor="retailer" />,
      filter_component: <></>,
    }
  ];

  // agency section is not available as of now, will be added later
  // if (["tenant", "agency", "cyreen"].includes(userType)) {
  //   tabsConfig.push(
  //     {
  //       label: "Agency",
  //       icon: "",
  //       unique_key: "agencyUsersList",
  //       user_type: "agency",
  //       dashboard: <UserList usedFor="agency" />,
  //       filter_component: <></>,
  //     }
  //   );
  // }

  // Tenant tab follows the tenant edit flag. The Cyreen tab below still uses user_list_can_edit_cyreen.
  if (["tenant", "cyreen"].includes(userType) && permissionScope.user_list_can_edit_tenant === true) {
    tabsConfig.push(
      {
        label: "Tenant",
        icon: "",
        unique_key: "tenantUsersList",
        user_type: "tenant",
        dashboard: <TenantUserList />,
        filter_component: <></>,
      }
    );
  }

  if (permissionScope.user_list_can_edit_brand === true &&
    permissionScope.user_list_can_delete_brand === true && showBrandsSection === true) {
    tabsConfig.push(
      {
        label: "Brands",
        icon: "",
        unique_key: "brandUsersList",
        user_type: "brand",
        dashboard: <BrandUserlist usedFor="brand" />,
        filter_component: <></>,
      }
    );
  }

  if (userType === "cyreen" &&
    permissionScope.user_list_can_edit_cyreen === true &&
    permissionScope.user_list_can_delete_cyreen === true) {
    tabsConfig.push(
      {
        label: "Cyreen",
        icon: "",
        unique_key: "cyreenUsersList",
        user_type: "cyreen",
        dashboard: <UserList usedFor="cyreen" />,
        filter_component: <></>,
      }
    );
  }

  const showTabsView = tabsConfig.length > 1;

  const loadUsersForTab = async (requestedUserType) => {
    const normalizedUserType = normalizeUserListType(requestedUserType);

    if (loadedUserTypes.includes(normalizedUserType)) {
      return;
    }

    await fetchUsers(normalizedUserType);
    setLoadedUserTypes((previousTypes) => {
      return previousTypes.includes(normalizedUserType)
        ? previousTypes
        : [...previousTypes, normalizedUserType];
    });
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
    const selectedTab = tabsConfig[newValue];
    if (typeof selectedTab !== "undefined") {
      loadUsersForTab(selectedTab.user_type);
    }
  };

  const loadAllInfo = async () => {
    // fetchRoles: used to enable/disable edit/delete when the user's roles are not in the allowed list.
    // Company share-list is loaded only when Add/Update modals need the Company dropdown.
    setBatchLoadingStatus(true)
    const defaultTabType = typeof tabsConfig[0] !== "undefined" ? tabsConfig[0].user_type : "retailer";
    await Promise.all([
      fetchUsers(defaultTabType),
      fetchRoles(false),
    ]).then(() => {
      setLoadedUserTypes([normalizeUserListType(defaultTabType)]);
      setBatchLoadingStatus(false);
    });
    // scroll to the last position where user was before refreshing the page
    scrollTableListings("users_pagination");
  }

  React.useEffect(() => {
    // check pagination cookie and set it in redux state
    if (typeof document.cookie.split("; ").find((row) => row.startsWith("users_pagination")) !== "undefined") {
      const paginationObj = JSON.parse(
        document.cookie
          .split("; ")
          .find((row) => row.startsWith("users_pagination"))
          .split("=")[1]
      );
      manageUsersPaginationCookie(paginationObj);
    }
    // this will load the user list and roles
    loadAllInfo();
    // delete pagination cookies for other pages.
    deletePaginationCookies("users");

    // handle scroll event to save the scroll position in cookie
    // this will be used to scroll the page to the last position where user was before refreshing the page
    const handleScroll = () => {
      const currentScrollPos = window.pageYOffset;
      manageUsersPaginationCookie({ scroll_pos: currentScrollPos });
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };

  }, []);

  return (
    <>
      {/* ========== main component body =========== */}
      <StyledMainBox component="main">
        <Box height={30} />
        <h1>User List</h1>
        {/* ============ user tabs =========== */}
        {showTabsView === true ? (
          <>
            <Box
              sx={{
                display: "flex", // Make the Box a flex container
                position: "sticky",
                top: "0",
                zIndex: 1,
                backgroundColor: "#fff",
              }}
            >
              <Box component={"div"}>
                <StyledTabs value={value} onChange={handleChange} aria-label="data-exchange" textColor={"#F04B55"}>
                  {tabsConfig.map((tabDetail, index) => {
                    return (
                      <Tab key={tabDetail.unique_key} icon={tabDetail.icon} label={tabDetail.label} {...a11yProps(index)} />
                    );
                  })}
                </StyledTabs>
              </Box>
            </Box>
            {/* ============== tab contents =========== */}
            {tabsConfig.map((tabDetail, index) => {
              return (
                <CustomTabPanel key={tabDetail.unique_key} value={value} index={index}>
                  {tabDetail.dashboard}
                </CustomTabPanel>
              );
            })}
          </>
        ) : (
          <UserList usedFor={"retailer"} />
        )}
      </StyledMainBox>
    </>
  );
};

export default AccessControl(Users, ["user_list_can_view"], [], ["user_section", "user_list"]);
